import { EPhoneOperation } from '@/enums/customer';
import { EStatus } from '@/enums/common';
import { pureFetch, userFetch } from '../fetch';

import type {
  IAccount,
  IAccountAddress,
  IAccountBilling,
  IAccountShippingAddress,
  ICurrentUser,
  ISubAccount,
  ISubAccountItem,
  IOpenApiAddUserParams,
  IOpenApiAddUserData,
  IOpenApiResponse,
} from './types';
import type { IPagingParams, ITableData } from '@/types';

/**
 * 手机号注册时，获取验证码
 * @param data
 */
export const fetchCaptchaByMobileRegister = data => userFetch.post('/mall/captcha/send', data);

/**
 * 公司注册
 * @param data
 */
export const registerCompany = data => userFetch.post('/mall/company/register', data);

/**
 * 个人账号注册
 * @param data
 */
export const registerAccount = data => userFetch.post<void, string>('/mall/user/register', data);

/** 获取当前登录用户信息 */
export const fetchCurrentUser = () => userFetch.get<void, ICurrentUser>('/operator/getOperator');

/** 获取账户信息 */
export const fetchAccount = () => userFetch.post<void, IAccount>('/operator/mall/accountInfo');

/** 更新账户信息 */
export const updateAccountBaseInfo = (
  data: Pick<IAccount, 'email' | 'userName' | 'department' | 'job'> & { id: IAccount['operatorId'] }
) => userFetch.post<void, IAccount>('/operator/accountInfo/edit', data);

/** 发送手机操作【解绑、绑定】验证码  */
export const sendPhoneOperatorCaptcha = (data: { phone: string; type: EPhoneOperation }) =>
  userFetch.post('/operator/accountInfo/mobile/bind/sendCaptcha', data);

/** 更新账户手机【绑定，解绑】状态 */
export const updateAccountPhoneState = (data: {
  mobile: string;
  type: EPhoneOperation;
  id: string;
  captcha: string;
}) => userFetch.post('/operator/accountInfo/mobile/bind', data);

/** 获取收货地址列表 */
export const fetchShippingAddresses = (params: IPagingParams) =>
  userFetch.post<void, ITableData<IAccountShippingAddress>>(
    '/operator/mall/customerAddress',
    params
  );

/** 获取开票列表 */
export const fetchBillings = () =>
  userFetch.post<void, IAccountBilling[]>('/operator/mall/customerInvoice');

/** 获取子账号列表 */
export const fetchSubAccounts = (data: IPagingParams) =>
  userFetch.post<void, ITableData<ISubAccountItem>>(
    '/operator/accountManager/subAccount/page',
    data
  );

/** 更新子账户信息 */
export const updateSubAccount = (
  data: Omit<ISubAccount, 'nameZh' | 'userName'> & {
    password: string;
    checkPassword?: string;
    delAddressList?: string[];
  }
) => userFetch.post('/operator/accountManager/subAccount/editDetail', data);

/** 变更子账户状态 */
export const batchUpdateSubAccountStatus = (data: { enable: EStatus; ids: string[] }) =>
  userFetch.post('/operator/editStatus', data);

/** 查询子账号详情 */
export const fetchSubAccount = (data: Pick<ISubAccount, 'sunAccountId'>) =>
  userFetch.post<void, ISubAccount>('/operator/accountManager/subAccount/detail', data);

/** 查询一级公司地址列表 */
export const fetchAddressesOfAccount = data =>
  userFetch.post<void, IAccountAddress[]>('/operator/accountManager/getAddressList', data);

/** 更改密码 */
export const updatePassword = data => userFetch.post('/operator/accountInfo/changePassword', data);

/** 增加子账户地址 */
export const saveSubAccountAddress = data => userFetch.post('/operator/saveSubAddress', data);

/** 上传图片 */
export const uploadPicture = data => {
  const code = data.get('code');
  const mobile = data.get('mobile');
  data.delete('code');
  data.delete('mobile');
  userFetch.post(`/mall/upload?mobile=${mobile}&code=${code}`, data);
};

/** 增加子账号 */
export const addNewAccount = data => userFetch.post('/operator/accountManager/add', data);

/** 查询客户信控信息 */
export const queryCustomerCreditInfo = () => userFetch.post('/operator/mall/query/credit');

/** 添加认证信息 */
export const addInfo = data => userFetch.post('/mall/company/addInfo', data);

/** OpenAPI 新增用户 */
export const fetchOpenApiAddUser = (data: IOpenApiAddUserParams) =>
  userFetch.post<void, IOpenApiResponse<IOpenApiAddUserData>>('/openApiUser/add', data);
